import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Check, Clock3 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Kicker, SerifTitle } from "@/components/Section";

/**
 * InvestmentSection — refined, scannable "Starting at" pricing cards.
 *
 * Default card view (under 10 seconds to scan):
 *   - Name
 *   - Starting price
 *   - Timing
 *   - Best for (short)
 *   - One positioning sentence
 *   - CTA
 * Expandable "Details" shows the full inclusions list.
 *
 * props:
 *   kicker, title, subtitle
 *   packages: [{ id, name, starting, timing?, badge?, bestFor, positioning, includes[], cta, sessionType, format? }]
 *   reassurance, customNote, bookingNote (optional)
 *   className (optional)
 *   columns (3 or 4)
 */
export default function InvestmentSection({
    kicker = "Investment",
    title = "A considered menu of experiences.",
    subtitle,
    packages = [],
    reassurance,
    customNote,
    bookingNote,
    className,
    columns = 4,
    id = "investment",
}) {
    const colMap = {
        3: "lg:grid-cols-3",
        4: "lg:grid-cols-4",
    };
    return (
        <section id={id} data-testid="investment-section" className={cn("scroll-mt-24 py-14 sm:py-20 lg:py-28 border-t border-border", className)}>
            <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
                    <div className="lg:col-span-7">
                        <Kicker>{kicker}</Kicker>
                        <SerifTitle className="mt-3">{title}</SerifTitle>
                    </div>
                    {subtitle && (
                        <div className="lg:col-span-5">
                            <p className="text-sm leading-[1.85] text-muted-foreground max-w-[48ch]">{subtitle}</p>
                        </div>
                    )}
                </div>

                <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5", colMap[columns] || "lg:grid-cols-4")}>
                    {packages.map((p) => (
                        <PricingCard key={p.id} pkg={p} highlighted={!!p.badge} />
                    ))}
                </div>

                {(reassurance || customNote || bookingNote) && (
                    <div className="mt-12 bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-7 sm:p-9 space-y-5">
                        {reassurance && (
                            <div>
                                <div className="kicker">A fully guided experience</div>
                                <p className="mt-2 text-sm leading-[1.85] text-foreground/85 max-w-[78ch]">{reassurance}</p>
                            </div>
                        )}
                        {customNote && (
                            <>
                                <Separator className="bg-border" />
                                <div>
                                    <div className="kicker">Custom Quoted</div>
                                    <p className="mt-2 text-sm leading-[1.85] text-muted-foreground max-w-[78ch]">{customNote}</p>
                                </div>
                            </>
                        )}
                        {bookingNote && (
                            <>
                                <Separator className="bg-border" />
                                <div>
                                    <div className="kicker">Booking</div>
                                    <p className="mt-2 text-sm leading-[1.85] text-muted-foreground max-w-[78ch]">{bookingNote}</p>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

export function PricingCard({ pkg, highlighted = false, compact = false }) {
    const [open, setOpen] = useState(false);
    const ctaHref = `/inquire${pkg.sessionType ? `?type=${encodeURIComponent(pkg.sessionType)}` : ""}`;

    return (
        <article
            data-testid={`pricing-card-${pkg.id}`}
            className={cn(
                "relative flex flex-col h-full bg-background border rounded-[3px] p-6 sm:p-7 transition-all",
                highlighted
                    ? "border-[hsl(var(--lux-oxblood))] shadow-[0_24px_60px_-30px_rgba(74,24,32,0.35)] ring-1 ring-[hsl(var(--lux-oxblood))]/15"
                    : "border-border hover:border-foreground/40"
            )}
        >
            {pkg.badge && (
                <span
                    data-testid={`pricing-badge-${pkg.id}`}
                    className="absolute -top-3 left-6 inline-flex items-center bg-[hsl(var(--lux-oxblood))] text-[hsl(var(--lux-porcelain))] text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 rounded-full"
                >
                    {pkg.badge}
                </span>
            )}

            {/* Name */}
            <h3 className="font-serif-display text-2xl leading-tight mt-1">{pkg.name}</h3>

            {/* Price */}
            <div className="mt-3 text-[11px] tracking-[0.16em] uppercase text-[hsl(var(--lux-oxblood))]">
                {pkg.starting}
            </div>

            {/* Timing (if present) */}
            {pkg.timing && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] tracking-[0.06em] text-muted-foreground">
                    <Clock3 className="h-3 w-3 text-[hsl(var(--lux-brass))]" />
                    <span>{pkg.timing}</span>
                </div>
            )}

            <Separator className="my-5 bg-[hsl(var(--lux-brass))]/45 w-8" />

            {/* Best for — short */}
            {pkg.bestFor && (
                <>
                    <div className="kicker">Best for</div>
                    <p className="mt-1.5 text-[13px] leading-[1.7] text-foreground/85">{pkg.bestFor}</p>
                </>
            )}

            {/* Positioning sentence */}
            {pkg.positioning && (
                <p className="mt-3 text-[13px] leading-[1.7] text-muted-foreground">{pkg.positioning}</p>
            )}

            {/* Mentoring-style format field (kept for back-compat) */}
            {pkg.format && (
                <div className="mt-4">
                    <div className="kicker">Format</div>
                    <p className="mt-1 text-[13px]">{pkg.format}</p>
                </div>
            )}

            {/* Expandable "What's included" / Details */}
            {Array.isArray(pkg.includes) && pkg.includes.length > 0 && !compact && (
                <div className="mt-5">
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        data-testid={`pricing-toggle-${pkg.id}`}
                        aria-expanded={open}
                        className="flex items-center justify-between w-full text-[11px] tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground"
                    >
                        <span>{open ? "Hide details" : "Details"}</span>
                        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
                    </button>
                    {open && (
                        <ul className="mt-4 space-y-2 text-[13px]">
                            {pkg.includes.map((it) => (
                                <li key={it} className="flex items-start gap-2">
                                    <Check className="h-3.5 w-3.5 mt-1.5 text-[hsl(var(--lux-brass))] shrink-0" />
                                    <span className="leading-[1.6]">{it}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <div className="mt-auto pt-6">
                <Link
                    to={ctaHref}
                    data-testid={`pricing-cta-${pkg.id}`}
                    className={cn("btn-request btn-request--sm w-full justify-center", !highlighted && "btn-request--ghost")}
                >
                    {pkg.cta || "Request Availability"}
                </Link>
            </div>
        </article>
    );
}

export function CompactTierList({ tiers, ctaLabel, ctaTo, secondaryCtaLabel, secondaryCtaTo, kicker, title, subtitle }) {
    return (
        <div className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[3px] p-7 sm:p-9">
            {kicker && <div className="kicker">{kicker}</div>}
            {title && <h3 className="font-serif-display text-2xl sm:text-3xl mt-3 leading-tight">{title}</h3>}
            {subtitle && <p className="mt-3 text-sm leading-[1.85] text-muted-foreground max-w-[58ch]">{subtitle}</p>}
            <ul className="mt-7 divide-y divide-border border-y border-border">
                {tiers.map((t) => (
                    <li key={t.id} className="py-5 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                        <div>
                            <div className="font-serif-display text-lg leading-tight">{t.name}</div>
                            {t.note && <div className="text-xs text-muted-foreground mt-1">{t.note}</div>}
                            {t.bestFor && <div className="text-xs text-muted-foreground mt-1">{t.bestFor}</div>}
                        </div>
                        <div className="text-[11px] tracking-[0.16em] uppercase text-[hsl(var(--lux-oxblood))] whitespace-nowrap">
                            {t.starting}
                        </div>
                    </li>
                ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
                {ctaTo && <Link to={ctaTo} className="btn-request btn-request--sm">{ctaLabel}</Link>}
                {secondaryCtaTo && <Link to={secondaryCtaTo} className="btn-request btn-request--sm btn-request--ghost">{secondaryCtaLabel}</Link>}
            </div>
        </div>
    );
}

/**
 * AddOnsSection — clean add-on grid (no expandable details).
 * Each addon: { id, name, starting, bestFor, sessionType, isLinkBack? }
 */
export function AddOnsSection({ kicker, title, subtitle, addons = [], ctaLabel, ctaSessionType, className, id }) {
    return (
        <section id={id} data-testid={`addons-${id || "section"}`} className={cn("scroll-mt-24 py-14 sm:py-20 lg:py-28 border-t border-border", className)}>
            <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                    <div className="lg:col-span-7">
                        {kicker && <Kicker>{kicker}</Kicker>}
                        {title && <SerifTitle className="mt-3">{title}</SerifTitle>}
                    </div>
                    {subtitle && (
                        <div className="lg:col-span-5">
                            <p className="text-sm leading-[1.85] text-muted-foreground max-w-[48ch]">{subtitle}</p>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                    {addons.map((a) => (
                        <article
                            key={a.id}
                            data-testid={`addon-card-${a.id}`}
                            className={cn(
                                "bg-background border rounded-[3px] p-6 sm:p-7 flex flex-col h-full transition-colors",
                                a.isLinkBack ? "border-dashed border-[hsl(var(--lux-brass))]/60" : "border-border hover:border-foreground/40"
                            )}
                        >
                            <h3 className="font-serif-display text-xl leading-tight">{a.name}</h3>
                            <div className="mt-2 text-[11px] tracking-[0.16em] uppercase text-[hsl(var(--lux-oxblood))]">{a.starting}</div>
                            <Separator className="my-4 bg-[hsl(var(--lux-brass))]/45 w-8" />
                            <div className="kicker">Best for</div>
                            <p className="mt-1.5 text-[13px] leading-[1.7] text-foreground/85">{a.bestFor}</p>
                            <Link
                                to={`/inquire?type=${encodeURIComponent(a.sessionType || "Photo + Video Content")}`}
                                data-testid={`addon-cta-${a.id}`}
                                className="btn-request btn-request--sm btn-request--ghost mt-auto pt-6 justify-center"
                            >
                                Add to my session
                            </Link>
                        </article>
                    ))}
                </div>

                {ctaLabel && (
                    <div className="mt-8 text-center">
                        <Link
                            to={`/inquire?type=${encodeURIComponent(ctaSessionType || "Photo + Video Content")}`}
                            data-testid={`addons-primary-cta-${id || "section"}`}
                            className="btn-request btn-request--sm"
                        >
                            {ctaLabel}
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}

/**
 * CustomSolutions — small secondary grid for "Custom Business Solutions".
 * Visually deprioritised vs. main package cards.
 */
export function CustomSolutions({ kicker, title, subtitle, items = [], className, id }) {
    return (
        <section id={id} data-testid={`custom-solutions-${id || "section"}`} className={cn("py-14 sm:py-20 border-t border-border", className)}>
            <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-10">
                <div className="max-w-[760px] mb-8">
                    {kicker && <Kicker>{kicker}</Kicker>}
                    {title && <h2 className="font-serif-display text-2xl sm:text-3xl mt-3 leading-tight">{title}</h2>}
                    {subtitle && <p className="mt-3 text-sm leading-[1.85] text-muted-foreground max-w-[58ch]">{subtitle}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                    {items.map((it) => (
                        <article
                            key={it.id}
                            data-testid={`custom-card-${it.id}`}
                            className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[3px] p-6 sm:p-7 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5"
                        >
                            <div className="flex-1">
                                <h3 className="font-serif-display text-xl leading-tight">{it.name}</h3>
                                <div className="mt-2 text-[11px] tracking-[0.16em] uppercase text-[hsl(var(--lux-oxblood))]">{it.starting}</div>
                                <p className="mt-3 text-[13px] leading-[1.7] text-muted-foreground max-w-[48ch]">{it.bestFor}</p>
                            </div>
                            <Link
                                to={`/inquire?type=${encodeURIComponent(it.sessionType || "")}`}
                                data-testid={`custom-cta-${it.id}`}
                                className="btn-request btn-request--sm btn-request--ghost whitespace-nowrap self-start"
                            >
                                {it.cta || "Request Availability"}
                            </Link>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
